import { useEffect } from 'react';

/**
 * Cross-platform keyboard shortcut hook.
 *
 * Use the literal `cmd+` prefix; it is mapped to `meta` on macOS and
 * `ctrl` on other platforms automatically.
 *
 * @example
 *   useKeyboardShortcut('cmd+k', () => setOpen(true));
 *   useKeyboardShortcut('shift+/', handler);
 */
export function useKeyboardShortcut(
  shortcut: string,
  handler: (e: KeyboardEvent) => void,
  options: { enabled?: boolean; target?: Window | HTMLElement | null } = {},
): void {
  const { enabled = true, target } = options;

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const isMac =
      typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');

    const parts = shortcut
      .toLowerCase()
      .split('+')
      .map((p) => p.trim());

    const key = parts[parts.length - 1] ?? '';
    const mods = new Set(parts.slice(0, -1));

    const wantCmd = mods.has('cmd') || mods.has('meta');
    const wantCtrl = mods.has('ctrl');
    const wantShift = mods.has('shift');
    const wantAlt = mods.has('alt') || mods.has('option');

    const onKeyDown = (e: KeyboardEvent) => {
      const pressed = (e.key || '').toLowerCase();
      if (pressed !== key) return;

      const cmdOk = wantCmd ? (isMac ? e.metaKey : e.ctrlKey) : true;
      const ctrlOk = wantCtrl ? e.ctrlKey : true;
      const shiftOk = wantShift ? e.shiftKey : !e.shiftKey || wantShift;
      const altOk = wantAlt ? e.altKey : !e.altKey || wantAlt;

      // If no cmd/ctrl was requested, ensure neither is pressed (avoid
      // hijacking browser shortcuts unintentionally).
      const noModWanted = !wantCmd && !wantCtrl;
      if (noModWanted && (e.metaKey || e.ctrlKey)) return;

      if (cmdOk && ctrlOk && shiftOk && altOk) {
        handler(e);
      }
    };

    const el: Window | HTMLElement = (target as HTMLElement) ?? window;
    el.addEventListener('keydown', onKeyDown as EventListener);
    return () => el.removeEventListener('keydown', onKeyDown as EventListener);
  }, [shortcut, handler, enabled, target]);
}
